// useStats — TanStack Query hook for the admin Dashboard aggregates.
//
// Guards on authenticated status AND on the role. Polls every 60s so the
// overview KPIs reflect new payouts without a manual reload.
//
// The query key carries currentMerchantId. apiClient already sends
// X-Merchant-ID on every request once a merchant is picked and the backend
// scopes /v1/stats to it — but with a fixed key, switching merchants served
// the previous merchant's cached numbers until the next poll, so every
// merchant looked the same. One cache entry per merchant (and one for the
// platform-wide view) is what makes the dashboard follow the switcher.
//
// The role guard is not an optimisation. GET /v1/stats is admin-only on
// the backend (auth.RequireRole(auth.RoleAdmin)), and this hook used to
// fire for anyone merely authenticated. The Dashboard gates the RENDERING
// of the stats on isAdminRole, but the request went out regardless — so
// every merchant user who opened the dashboard sent a request that could
// only ever 403, and kept re-sending it on the poll interval. Nine of
// them from four different IPs showed up in the production log.
//
// Nothing was visibly broken (the tiles were hidden anyway), which is
// precisely why it survived: a permission denial that nobody sees is
// still a permission denial in the audit trail, and it makes a real 403
// impossible to spot among the noise.
import { useQuery } from "@tanstack/react-query";

import { getStats } from "@/api/stats";
import { useAuth, effectiveRole, isAdminRole } from "@/store/auth";
import { useUI } from "@/store/ui";
import type { DateRangeBounds } from "@/utils/dateRange";

export function useStats(range: DateRangeBounds = {}) {
  const status = useAuth((s) => s.status);
  const role = useAuth((s) => effectiveRole(s));
  const currentMerchantId = useUI((s) => s.currentMerchantId);

  return useQuery({
    queryKey: ["stats", currentMerchantId, range.from ?? null, range.to ?? null],
    queryFn: () => getStats(range),
    enabled: status === "authenticated" && isAdminRole(role),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}
